import React from "react";
import { connect } from "react-redux";
import { Dropdown, DropdownToggle, DropdownItem, DropdownMenu, Row, Col } from "reactstrap";
import { Icon } from "@cadmakers-ui/icons";
import { ThunkDispatch } from "redux-thunk";
import { IAppState } from "../../../Common";
import { Application } from "../../../App";
import styles from "../../Styles/SearchBarContextSelector.module.scss";
import { SearchContext, AppSearchContext, TopicSearchContext } from "../../Models/SearchContext";
import { SearchContextType } from "../../Constants/LayoutEnums";
import { allContext, topicContext, appContext } from "../../Reducers/SearchContextReducer";
import {
  ISetSelectedSearchContextArguments,
  SearchContextAction,
  setSelectedSearchContext
} from "../../Actions/SearchContextActions";
import { Topic } from "../../../Topic/Models/Topic";
import iconStyles from "../../../Common/Styles/Icons.module.scss";

const uuid = require("uuid");

type Props = {
  shouldShow: boolean;
  onActiveSearchContextChange: (context: SearchContext) => void;
};

type StateProps = {
  areAppsLoaded: boolean;
  availableApps: Application[];
  activeApp?: Application;
  activeTopic?: Topic;
  selectedContext: SearchContext;
  avaliableTopics: Topic[];
  areTopicsLoaded: boolean;
};

type DispatchProps = {
  setSelectedSearchContext: (args: ISetSelectedSearchContextArguments) => void;
};

type State = {
  availableContexts: SearchContext[];
  didLoadApps: boolean;
  didLoadDefaultContext: boolean;
  isOpen: boolean;
  didLoadTopics: boolean;
  didLoadHeaders: boolean;
  previousActiveApp?: Application;
  previousActiveTopic?: Topic;
};

function mapStateToProps(state: IAppState): StateProps {
  return {
    areAppsLoaded: state.app.appsLoadingState.isLoaded,
    availableApps: state.app.applications,
    activeApp: state.app.appsLoadingState.isLoaded ? state.app.activeApp : undefined,
    activeTopic: state.app.topicsLoadingState.isLoaded ? state.app.activeTopic : undefined,
    selectedContext: state.searchContext.selectedContext,
    avaliableTopics: state.app.topics,
    areTopicsLoaded: state.app.topicsLoadingState.isLoaded
  };
}

function mapDispatchToProps(
  dispatch: ThunkDispatch<IAppState, {}, SearchContextAction>
): DispatchProps {
  return {
    setSelectedSearchContext: (args: ISetSelectedSearchContextArguments) =>
      dispatch(setSelectedSearchContext(args))
  };
}

class SearchBarContextSelector extends React.Component<Props & StateProps & DispatchProps, State> {
  constructor(props: Props & StateProps & DispatchProps) {
    super(props);

    this.state = {
      availableContexts: [],
      didLoadApps: false,
      didLoadDefaultContext: false,
      isOpen: false,
      didLoadTopics: false,
      didLoadHeaders: false
    };

    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.onClickDropdownItem = this.onClickDropdownItem.bind(this);
  }

  componentDidUpdate() {
    this.loadAppContexts();
    this.loadTopicContexts();
    this.updateForActiveItem();
  }

  loadAppContexts = () => {
    if (this.props.areAppsLoaded && !this.state.didLoadApps) {
      this.setState(state => {
        // make a search context for all apps loaded
        const appContexts: AppSearchContext[] = [];
        for (let i = 0; i < this.props.availableApps.length; i++) {
          appContexts.push({
            type: SearchContextType.App,
            name: this.props.availableApps[i].name,
            uniqueId: `search_app_${this.props.availableApps[i].rolePrefix}`,
            itemId: this.props.availableApps[i].rolePrefix
          });
        }
        return {
          ...state,
          didLoadApps: true,
          // load available contexts
          availableContexts: state.availableContexts.concat(appContexts)
        };
      });
    }
  };

  loadTopicContexts = () => {
    if (this.props.areTopicsLoaded && !this.state.didLoadTopics) {
      this.setState(state => {
        const topicContexts: TopicSearchContext[] = [];
        this.props.avaliableTopics.forEach(topic => {
          topicContexts.push({
            type: SearchContextType.Topic,
            name: topic.name,
            itemId: topic.code,
            uniqueId: `search_topic${topic.code}`
          });
        });

        return {
          ...state,
          didLoadTopics: true,
          availableContexts: state.availableContexts.concat(topicContexts)
        };
      });
    }
  };

  // This will update the selected active app/topic when those are changed in the "projects" dropdown (in the top right)
  updateForActiveItem = () => {
    if (this.state.didLoadApps && this.state.didLoadTopics) {
      if (this.props.activeApp) {
        // update the active app if it changed
        if (
          !this.state.previousActiveApp ||
          this.props.activeApp.rolePrefix !== this.state.previousActiveApp.rolePrefix
        ) {
          // this one is changed, update the selected context
          const { activeApp } = this.props;
          this.setState(
            (prevState: State) => ({
              previousActiveApp: activeApp
            }),
            () => {
              this.selectAppContextWithRolePrefix(activeApp.rolePrefix);
            }
          );
        }
      } else if (this.props.activeTopic) {
        // update the active topic if it changed
        if (
          !this.state.previousActiveTopic ||
          this.props.activeTopic.code !== this.state.previousActiveTopic.code
        ) {
          // this one is changed, update the selected context
          const { activeTopic } = this.props;
          this.setState(
            (prevState: State) => ({
              previousActiveTopic: activeTopic
            }),
            () => {
              this.selectTopicContextWithCode(activeTopic.code);
            }
          );
        }
      }
    }
  };

  toggleDropdown = () => {
    this.setState(prevState => ({
      ...prevState,
      isOpen: !prevState.isOpen
    }));
  };

  onClickDropdownItem = (item: SearchContext) => {
    // update selected search context
    this.props.setSelectedSearchContext({ searchContext: item });

    // signal parent
    this.props.onActiveSearchContextChange(item);
  };

  createAllItem = () => {
    return (
      <DropdownItem
        key={allContext.uniqueId}
        onClick={() => this.onClickDropdownItem(allContext)}
        active={
          !!this.props.selectedContext &&
          this.props.selectedContext.uniqueId === allContext.uniqueId
        }
      >
        <span className={styles.itemHeaderText}>{allContext.name}</span>
      </DropdownItem>
    );
  };

  createAppItem = () => {
    return (
      <DropdownItem
        key={appContext.uniqueId}
        onClick={() => this.onClickDropdownItem(appContext)}
        active={
          !!this.props.selectedContext &&
          this.props.selectedContext.uniqueId === appContext.uniqueId
        }
      >
        <span className={styles.itemHeaderText}>{appContext.name}</span>
      </DropdownItem>
    );
  };

  createTopicItem = () => {
    return (
      <DropdownItem
        key={topicContext.uniqueId}
        onClick={() => this.onClickDropdownItem(topicContext)}
        active={
          !!this.props.selectedContext &&
          this.props.selectedContext.uniqueId === topicContext.uniqueId
        }
      >
        <span className={styles.itemHeaderText}>{topicContext.name}</span>
      </DropdownItem>
    );
  };

  createContextItem = (context: SearchContext) => {
    return (
      <DropdownItem
        key={context.uniqueId}
        onClick={() => this.onClickDropdownItem(context)}
        active={
          !!this.props.selectedContext && this.props.selectedContext.uniqueId === context.uniqueId
        }
      >
        {context.name}
      </DropdownItem>
    );
  };

  createDropdownItems = (contextType: SearchContextType) => {
    return this.state.availableContexts
      .filter(c => c.type === contextType)
      .sort((a, b) => (a.name as string).localeCompare(b.name as string))
      .map(context => this.createContextItem(context));
  };

  createAppDropdownItems = () => {
    return this.createDropdownItems(SearchContextType.App);
  };

  createTopicDropdownItems = () => {
    return this.createDropdownItems(SearchContextType.Topic);
  };

  createDropdownSeparator = () => {
    return (
      <DropdownItem key={uuid()} className={styles.itemSeparatorContainer} header>
        <div className={styles.itemSeparator} />
      </DropdownItem>
    );
  };

  getDropdownItems = () => {
    return [
      this.createAllItem(),
      this.createDropdownSeparator(),
      this.createAppItem(),
      this.createAppDropdownItems(),
      this.createDropdownSeparator(),
      this.createTopicItem(),
      this.createTopicDropdownItems()
    ];
  };

  selectAppContextWithRolePrefix(rolePrefix: string) {
    // find the context that matches the role prefix
    if (this.state.availableContexts) {
      for (let i = 0; i < this.state.availableContexts.length; i++) {
        if (this.state.availableContexts[i].uniqueId === `search_app_${rolePrefix}`) {
          // we found the context so set it to selected
          this.props.setSelectedSearchContext({
            searchContext: this.state.availableContexts[i]
          });
          this.props.onActiveSearchContextChange(this.state.availableContexts[i]);
          return;
        }
      }
    }
  }

  selectTopicContextWithCode(code: string) {
    // find the context that matches the code
    if (this.state.availableContexts) {
      for (let i = 0; i < this.state.availableContexts.length; i++) {
        if (this.state.availableContexts[i].uniqueId === `search_topic${code}`) {
          // we found the context so set it to selected
          this.props.setSelectedSearchContext({
            searchContext: this.state.availableContexts[i]
          });
          this.props.onActiveSearchContextChange(this.state.availableContexts[i]);
          return;
        }
      }
    }
  }

  render() {
    if (this.props.areAppsLoaded && this.props.areTopicsLoaded && this.props.shouldShow) {
      return (
        <div className={styles.searchBarContextSelectorContainer}>
          {(!!this.props.activeApp || !!this.props.activeTopic) && (
            <Dropdown
              className={styles.dropdown}
              isOpen={this.state.isOpen}
              toggle={this.toggleDropdown}
            >
              <DropdownToggle className={styles.dropdownToggle}>
                <Row>
                  <Col sm="10" className={styles.contextNameCol}>
                    {!!this.props.selectedContext && this.props.selectedContext.name}
                  </Col>
                  <Col sm="2" className={styles.caratCol}>
                    <Icon
                      appName="GeneralLayout"
                      assetName="GENERAL_L_ArrowDown"
                      className={iconStyles.tinyIcon}
                    />
                  </Col>
                </Row>
              </DropdownToggle>
              <DropdownMenu className={styles.dropdownMenu}>{this.getDropdownItems()}</DropdownMenu>
            </Dropdown>
          )}
        </div>
      );
    }
    return null;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchBarContextSelector);
